import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { IconButton } from '@producer/ui-kit';
import { Pencil1Icon, PlusIcon } from '@radix-ui/react-icons';

import { Step } from '../../../app/entities/step';
import { Stripboard } from '../../../app/entities/stripboard';
import { formatDate } from '../../../lib/utils/DateHelper';
import { dateShort } from '../../../assets/contants/dates-fns';
import { RenameStepModal } from '../../process/components/rename-step.modal';
import { timeTo12HourFormat, timeTo24HourFormat } from '../../../lib/utils/Convertor';
import { InlineTimeSelector } from './InlineTimeSelector';
import { BaseRow, Cell, RowMirror } from './styled-strips';
import { Shot } from '../../../app/entities/shot';
import { Tooltip } from '../../../components/tooltip/Tooltip';

import type { DayBreakStrip } from '../../../app/entities/strip';

import styles from './day-start-strip.module.scss';
export type DayStartStripProps = {
  strip: DayBreakStrip;
  stripboard: Stripboard;
  isShotList?: boolean;
  startDate?: string;
};

export const DayStartStripCore: React.FC<DayStartStripProps & { shootingDay: Step }> = observer(
  ({ strip, stripboard, shootingDay, startDate }) => {
    const { dod } = strip.data || {};
    const [showNameModal, setShowNameModal] = useState(false);

    const handleChangeTime = async (value: { hour: number; minute: number }) => {
      const time = timeTo24HourFormat(value!);
      const { hour, minute } = time || {};

      await shootingDay.update({
        info: {
          generalCallTime: {
            hour,
            minute,
          },
        },
      });
    };

    const formattedDate = startDate ? formatDate(startDate, dateShort) : '';

    return (
      <>
        <RowMirror className={styles['wrapper']}>
          <div className={styles['name-wrapper']}>
            <h3 className={styles['day-name']}>
              (#{dod}) {shootingDay.name}
            </h3>

            <IconButton
              icon={<Pencil1Icon color="#fff" />}
              onClick={() => setShowNameModal(true)}
            />
          </div>

          <div className={styles['center-wrapper']}>
            <p className={styles['day-text']}>{formattedDate}</p>

            {shootingDay.info?.generalCallTime && (
              <InlineTimeSelector
                time={timeTo12HourFormat(shootingDay.info?.generalCallTime)}
                textToDisplay="General call time "
                backgroundVisible
                style={{ margin: '0' }}
                onChange={handleChangeTime}
              />
            )}
          </div>

          <p className={styles['dod-text']}>
            Day {dod} of {stripboard.totalShootingDays}
          </p>

          {shootingDay.info?.generalCallTime && (
            <div className={styles['inline-time-wrapper']}>
              <InlineTimeSelector
                backgroundVisible
                time={timeTo12HourFormat(shootingDay.info.generalCallTime)}
                style={{ margin: '0' }}
                onChange={handleChangeTime}
              />
            </div>
          )}

          <div className={styles['action-menu-wrapper']}>
            <Tooltip text={'Add shot'}>
              <IconButton
                icon={<PlusIcon />}
                onClick={() => {
                  Shot.createShot({
                    projectId: stripboard.projectId,
                    stripPosition: stripboard.getPreviousShootingDayPosition(strip._id),
                    shootingDayId: shootingDay._id,
                  });
                }}
              />
            </Tooltip>
          </div>
        </RowMirror>

        {showNameModal && (
          <RenameStepModal onCancel={() => setShowNameModal(false)} step={shootingDay} />
        )}
      </>
    );
  },
);

export const DayStartStrip: React.FC<DayStartStripProps & {}> = observer(
  ({ strip, stripboard }) => {
    const shootingDay = strip ? Step.getOne(strip.data.stepId) : undefined;

    if (!shootingDay) return null;

    return (
      <BaseRow className={styles['row']}>
        <Cell colSpan={100}>
          <DayStartStripCore
            stripboard={stripboard}
            strip={strip}
            shootingDay={shootingDay}
            startDate={shootingDay.dueDate}
          />
        </Cell>
      </BaseRow>
    );
  },
);
